import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { CircleAlert, X } from 'lucide-react'
import { forwardRef, HTMLAttributes, ReactNode, useState } from 'react'
import { Button } from './button'

const alertVariants = cva(
  'relative w-full rounded-lg justify-between flex items-center',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-100 [&_svg]:fill-primary [&_svg]:stroke-primary [&_line]:stroke-light text-primary',
        dark: 'bg-neutral-900 [&_svg]:fill-light [&_svg]:stroke-light [&_line]:stroke-neutral-900 text-light',
        secondary:
          'bg-secondary-50 [&_svg]:fill-secondary [&_svg]:stroke-secondary [&_line]:stroke-light text-secondary',
        neutral:
          'bg-neutral-100 [&_svg]:fill-neutral-900 [&_svg]:stroke-neutral-900 [&_line]:stroke-light text-neutral-900',
      },
      size: {
        sm: 'py-2 px-3 text-sm',
        md: 'py-2 px-4 text-base',
        lg: 'py-3 px-5 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

const Alert = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants> & {
      title: string | ReactNode
      titleClassName?: string
      icon?: ReactNode
      buttonProps?: {
        title: string
        onClick?: () => void
        className?: string
      }
      closable?: boolean
      onClose?: () => void
    }
>(
  (
    {
      className,
      variant,
      size,
      title,
      onClose,
      closable,
      titleClassName,
      icon,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true)

    if (!isVisible) return null

    return (
      <div
        ref={ref}
        role='alert'
        className={cn(alertVariants({ variant, size }), className)}
        {...Object.fromEntries(
          Object.entries(props).filter(
            ([key]) =>
              !['title', 'buttonProps', 'onClose', 'icon'].includes(key)
          )
        )}
      >
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center'>
            {icon ? (
              <div className='mr-2 flex h-5 w-5 items-center justify-center'>
                {icon}
              </div>
            ) : (
              <CircleAlert className='mr-2 h-5 w-5' />
            )}
            <p className={cn('mr-4', titleClassName)}>{title}</p>
          </div>
          {props?.buttonProps && (
            <Button
              {...props?.buttonProps}
              size='small'
              variant={variant === 'dark' ? 'white' : variant}
              className={props?.buttonProps?.className}
            >
              {props?.buttonProps?.title}
            </Button>
          )}
        </div>
        {closable && (
          <X
            onClick={() => {
              onClose?.()
              setIsVisible(false)
            }}
            className={cn(
              'h-5 w-5 cursor-pointer',
              variant === 'dark' ? 'fill-light' : 'fill-neutral-900'
            )}
          />
        )}
      </div>
    )
  }
)

Alert.displayName = 'Alert'

export { Alert }
